import React from "react";
import { motion, AnimatePresence } from "framer-motion";

import {
  Clock,
  Landmark,
  Coins,
  ShieldCheck,
  Trophy,
  Users,
  UserPlus,
  Headphones,
} from "lucide-react";

/* ================= DATA ================= */

const cards = [
  {
    icon: Clock,
    title: "Learn Anytime, 24x7",
    desc:
      "Adhyayi gives you complete flexibility to learn anytime, anywhere.",
    color: "bg-purple-100 text-purple-600 border-purple-200",
  },

  {
    icon: Landmark,
    title: "Government Exams",
    desc:
      "Prepare for SSC, Banking, Railway, UPSC and more in one place.",
    color: "bg-orange-100 text-orange-600 border-orange-200",
  },

  {
    icon: Coins,
    title: "Earn Credits & Cash",
    desc:
      "With Adhyayi, learning is not just about preparation. " +
      "Every correct answer helps you earn credits, which can be converted into real cash rewards.",
  },

  {
    icon: ShieldCheck,
    title: "Secure Platform",
    desc:
      "Transparent and secure learning experience for every student.",
    color: "bg-sky-100 text-sky-600 border-sky-200",
  },

  {
    icon: Trophy,
    title: "Daily Challenges",
    desc:
      "Compete in quizzes and challenges to boost your preparation.",
    color: "bg-fuchsia-100 text-fuchsia-600 border-fuchsia-200",
  },

  {
    icon: Users,
    title: "Trusted by Students",
    desc:
      "Thousands of aspirants trust Adhyayi for smart preparation.",
    color: "bg-cyan-100 text-cyan-600 border-cyan-200",
  },

  {
    icon: UserPlus,
    title: "Learning Community",
    desc:
      "Connect with students and stay motivated throughout preparation.",
    color: "bg-amber-100 text-amber-600 border-amber-200",
  },

  {
    icon: Headphones,
    title: "24x7 Support",
    desc:
      "Our support team is always available to help you instantly.",
    color: "bg-lime-100 text-lime-600 border-lime-200",
  },
];

/* ================= COMPONENT ================= */

const WhyChooseUs = () => {
  const [currentIndex, setCurrentIndex] = React.useState(0);

  /* 🔥 Auto Change Cards */
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 2) % cards.length);
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  const visibleCards = cards.slice(currentIndex, currentIndex + 2);

  if (visibleCards.length < 2) {
    visibleCards.push(cards[0]);
  }

  return (
    <section className="w-full py-16 bg-[#F8F8FF] overflow-x-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Heading */}
        <div className="text-center mb-12">
          <p
            className="
              text-sm
              font-semibold
              uppercase
              tracking-[0.35em]
              text-slate-500
              mb-2
            "
          >
            Why Adhyayi?
          </p>

          <h2
            className="
              text-2xl
              sm:text-3xl
              lg:text-4xl
              font-bold
              text-slate-900
              leading-snug
            "
          >
            Smart Learning for a Better{" "}
            <span className="text-secondary">Tomorrow</span>
          </h2>
        </div>

        {/* 🔥 MOBILE ANIMATION */}
        <div className="block lg:hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              className="flex flex-col gap-4 "
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              {visibleCards.map((item, index) => {
                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{
                      opacity: 0,
                      x: index === 0 ? -100 : 100,
                    }}
                    animate={{
                      opacity: 1,
                      x: 0,
                    }}
                    exit={{
                      opacity: 0,
                      x: index === 0 ? -100 : 100,
                    }}
                    transition={{
                      duration: 0.7,
                      ease: "easeOut",
                    }}
                    className="
                      w-full
                      rounded-[2rem]
                      border
                      border-slate-200
                      bg-white
                      p-4
                      shadow-[0_20px_60px_rgba(15,23,42,0.08)]
                    "
                  >
                    {/* Icon */}
                    <div
                      className={`
                        mb-4
                        inline-flex
                        h-12
                        w-12
                        items-center
                        justify-center
                        rounded-2xl
                        border
                        ${item.color}
                      `}
                    >
                      <Icon size={22} />
                    </div>

                    {/* Title */}
                    <h3
                      className="
                        text-[15px]
                        font-semibold
                        text-slate-900
                        mb-2
                        leading-snug
                      "
                    >
                      {item.title}
                    </h3>

                    {/* Description */}
                    <p
                      className="
                        text-[12px]
                        leading-5
                        text-slate-600
                      "
                    >
                      {item.desc}
                    </p>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* 🔥 DESKTOP GRID */}
        <div
          className="
            hidden
            lg:grid
            lg:grid-cols-4
            gap-6
          "
        >
          {cards.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 40,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.08,
                }}
                whileHover={{
                  y: -6,
                }}
                className="
                  rounded-[2rem]
                  border
                  border-slate-200
                  bg-white
                  p-6
                  shadow-[0_20px_60px_rgba(15,23,42,0.08)]
                "
              >
                {/* Icon */}
                <div
                  className={`
                    mb-5
                    inline-flex
                    h-14
                    w-14
                    items-center
                    justify-center
                    rounded-3xl
                    border
                    ${item.color}
                  `}
                >
                  <Icon size={26} />
                </div>

                {/* Title */}
                <h3
                  className="
                    text-lg
                    font-semibold
                    text-slate-900
                    mb-3
                  "
                >
                  {item.title}
                </h3>

                {/* Desc */}
                <p
                  className="
                    text-sm
                    leading-6
                    text-slate-600
                  "
                >
                  {item.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;